/**
 * Sincronização do catálogo (PLAN §13).
 *
 * Fluxo local-first: a UI abre imediatamente com o que está no IndexedDB, e a
 * checagem de atualização acontece em segundo plano. Nenhuma tela espera a rede.
 *
 *   abrir  ──► carregar catálogo local  ──► renderizar
 *                        │
 *                        └─(se online)─► buscar manifest remoto
 *                                             │
 *                                     mesma versão ──► nada
 *                                             │
 *                                    versão diferente
 *                                             │
 *                                    baixar → validar → aplicar em transação
 *                                             │
 *                                    estado pessoal preservado
 */
import {
  applyCatalog,
  getCatalogMetadata,
  markCatalogChecked,
  type ApplyCatalogResult,
} from '@/db/repositories/catalog';
import { CATALOG_BASE_URL, CatalogValidationError, fetchCatalog } from '@/db/seed';
import { catalogManifestSchema, type CatalogManifest } from '@/types/catalog';

export type SyncOutcome =
  | 'up-to-date'
  | 'updated'
  | 'seeded'
  | 'offline'
  | 'invalid-remote'
  | 'failed';

export interface SyncSummary {
  outcome: SyncOutcome;
  checkedAt: string;
  fromVersion?: string;
  toVersion?: string;
  changes?: {
    categories: number;
    signs: number;
    deactivated: number;
    mediaOutdated: number;
    preservedStates: number;
  };
  message?: string;
  error?: unknown;
}

/** Busca e valida o manifest remoto (F7-T03). */
export async function fetchRemoteManifest(signal?: AbortSignal): Promise<CatalogManifest> {
  const url = `${CATALOG_BASE_URL}/manifest.json`;

  const response = await fetch(url, {
    // O manifest é o gatilho da atualização; servi-lo do cache derrotaria o
    // propósito. O catalog.json em si pode vir do cache do Service Worker.
    cache: 'no-store',
    ...(signal ? { signal } : {}),
  });

  if (!response.ok) {
    throw new CatalogValidationError(`Manifest respondeu HTTP ${response.status}`);
  }

  const parsed = catalogManifestSchema.safeParse(await response.json());
  if (!parsed.success) {
    throw new CatalogValidationError('Manifest remoto é inválido', { cause: parsed.error });
  }

  return parsed.data;
}

export interface SyncOptions {
  signal?: AbortSignal;
  /** Aplica o catálogo remoto mesmo com o hash igual. Usado pelo reparo manual. */
  force?: boolean;
}

/**
 * Executa uma verificação de atualização.
 *
 * Estar offline **não é erro** (F7-T15): devolve `outcome: 'offline'` em silêncio.
 * O app foi feito para passar dias sem rede.
 */
export async function syncCatalog(options: SyncOptions = {}): Promise<SyncSummary> {
  const { signal, force = false } = options;
  const checkedAt = new Date().toISOString();

  const local = await getCatalogMetadata();

  let manifest: CatalogManifest;
  try {
    manifest = await fetchRemoteManifest(signal);
  } catch (error) {
    if (signal?.aborted) {
      return { outcome: 'failed', checkedAt, message: 'Verificação cancelada.' };
    }
    // Falha de rede com catálogo local presente é o caso normal no modo avião.
    if (local) {
      await markCatalogChecked();
      return {
        outcome: 'offline',
        checkedAt,
        fromVersion: local.catalogVersion,
        message: 'Sem conexão. O catálogo local continua disponível.',
      };
    }
    return {
      outcome: 'offline',
      checkedAt,
      message: 'Sem conexão e ainda sem catálogo local.',
      error,
    };
  }

  // Comparação por hash de conteúdo, não por versão: a versão é um rótulo, o hash
  // é o que realmente diz se algo mudou.
  if (local && !force && local.contentHash === manifest.contentHash) {
    await markCatalogChecked();
    return {
      outcome: 'up-to-date',
      checkedAt,
      fromVersion: local.catalogVersion,
      toVersion: manifest.catalogVersion,
    };
  }

  let applied: ApplyCatalogResult;
  try {
    // Validação completa acontece dentro de fetchCatalog, antes de qualquer escrita:
    // um catálogo remoto inválido não pode corromper a cópia local que funciona.
    const catalog = await fetchCatalog(signal);

    if (catalog.contentHash !== manifest.contentHash) {
      return {
        outcome: 'invalid-remote',
        checkedAt,
        ...(local ? { fromVersion: local.catalogVersion } : {}),
        message:
          'O catálogo e o manifest remotos não conferem entre si. ' +
          'A atualização foi descartada e o catálogo local permanece intacto.',
      };
    }

    applied = await applyCatalog(catalog);

    return {
      outcome: local ? 'updated' : 'seeded',
      checkedAt,
      ...(local ? { fromVersion: local.catalogVersion } : {}),
      toVersion: catalog.catalogVersion,
      changes: {
        categories: applied.categories,
        signs: applied.signs,
        deactivated: applied.deactivated.length,
        mediaOutdated: applied.mediaOutdated.length,
        preservedStates: applied.preservedStates,
      },
    };
  } catch (error) {
    if (error instanceof CatalogValidationError) {
      return {
        outcome: 'invalid-remote',
        checkedAt,
        ...(local ? { fromVersion: local.catalogVersion } : {}),
        message: error.message,
        error,
      };
    }
    return {
      outcome: 'failed',
      checkedAt,
      ...(local ? { fromVersion: local.catalogVersion } : {}),
      message: 'Não foi possível concluir a verificação.',
      error,
    };
  }
}

/** Texto pronto para a UI (F7-T12). */
export function describeSyncSummary(summary: SyncSummary): string {
  switch (summary.outcome) {
    case 'seeded':
      return `Catálogo carregado: ${summary.changes?.signs ?? 0} sinais em ${
        summary.changes?.categories ?? 0
      } categorias.`;

    case 'updated': {
      const parts = [`Catálogo atualizado para a versão ${summary.toVersion}`];
      if (summary.changes?.deactivated) {
        parts.push(`${summary.changes.deactivated} sinal(is) saíram do glossário`);
      }
      if (summary.changes?.mediaOutdated) {
        parts.push(`${summary.changes.mediaOutdated} vídeo(s) baixados ficaram desatualizados`);
      }
      return `${parts.join('. ')}.`;
    }

    case 'up-to-date':
      return 'O catálogo já está atualizado.';

    case 'offline':
      return summary.message ?? 'Sem conexão.';

    case 'invalid-remote':
      return summary.message ?? 'O catálogo remoto é inválido e foi descartado.';

    case 'failed':
      return summary.message ?? 'A verificação falhou.';
  }
}
